import React, { useEffect, useMemo, useReducer, useState } from "react";
import BsModal from "../Common/BsModal";
import TextInput from "../Common/TextInput";
import Notification from "../Common/Notification";
import ajaxBanking from "../../util/remote/ajaxBanking";
import VendorSelector from "../Transaction/VendorSelector";
import { useAccounts } from "../../DataStore/AccountsContext";
import { useTag } from "../../DataStore/TagContext";
import AccountingHint from "../Ledger/AccountingHint";

const reducer = (state, action) => {
  if (action.type === "RESET") return { ...initialState };
  return { ...state, [action.type]: action.value };
};

const initialState = {
  loading: false,

  // locked context (from modal params)
  txn_type: "Income", // Income | Expense | Contra (locked from prop)
  direction: "Incoming",
  bank_id: "",
  cash_account_id: "",
  ledger_account_id: "",
  tag_id: "",

  // core cheque fields
  cheque_date: "",
  amount: "",
  beneficiary_id: "",
  reference: "",
  narration: "",

  // contra only
  contra_cash_account_id: "",
};

const normalizeTxnType = (t) => {
  const v = String(t || "").trim().toLowerCase();
  if (v === "deposit") return "Income";
  if (v === "withdraw" || v === "withdrawal" || v === "payout") return "Expense";
  if (v === "income") return "Income";
  if (v === "expense") return "Expense";
  if (v === "contra") return "Contra";
  return "Income";
};

const titleFromTxnType = (txnType) => {
  if (txnType === "Income") return "New Cheque Deposit";
  if (txnType === "Expense") return "New Cheque Withdrawal";
  return "New Contra Transfer (Own Accounts)";
};

const toneFromTxnType = (txnType) => {
  if (txnType === "Income")
    return { bg: "rgba(40,167,69,0.12)", bd: "rgba(40,167,69,0.25)", icon: "fa fa-arrow-down", text: "text-success" };
  if (txnType === "Expense")
    return { bg: "rgba(220,53,69,0.10)", bd: "rgba(220,53,69,0.20)", icon: "fa fa-arrow-up", text: "text-danger" };
  return { bg: "rgba(23,162,184,0.10)", bd: "rgba(23,162,184,0.20)", icon: "fa fa-random", text: "text-info" };
};

const safeMoney = (n) => (Number(n || 0) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

/**
 * NewChequeTransactionModal
 * - txnType is LOCKED from modal param: "Deposit" | "Withdraw" | "Contra"
 * - bank is passed in from bank card click (contains account_info + balances)
 */
const NewChequeTransactionModal = ({
  show,
  onClose,
  buttonLabel = "Post Cheque",
  txnType = "Deposit",
  bank = null,
  currency = "",
  onSaved = () => {},
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [currentNotify, setNotify] = useState(null);
  const {getIncomeAccounts, incomeAccountListing, getExpenseAccounts, expenseAccountListing} = useAccounts();
  const {getTagListing, tagListing} = useTag();
  
  const lockedTxnType = useMemo(() => normalizeTxnType(txnType), [txnType]);
  const modalTitle = useMemo(() => titleFromTxnType(lockedTxnType), [lockedTxnType]);
  const headerTone = useMemo(() => toneFromTxnType(lockedTxnType), [lockedTxnType]);

  // bank-derived info (safe reads)
  const bankName = bank?.bank_name || bank?.bankName || "Selected Bank";
  const bankBranch = bank?.address || "";
  const bankAccountNumber = bank?.account_number || "";
  const ledgerAccountId = bank?.account_id || bank?.account_info?.account_id || "";
  const ledgerAccountName = bank?.account_info?.account_name || "Bank Ledger Account";
  const ledgerBalance = bank?.account_info?.balance?.amount || 0;
 
  // Apply locked txn type + bank context whenever modal opens / txnType changes / bank changes
  useEffect(() => {
    if (!show) return;

    // lock txn type
    dispatch({ type: "txn_type", value: lockedTxnType });

    // implied direction
    if (lockedTxnType === "Income") dispatch({ type: "direction", value: "Incoming" });
    if (lockedTxnType === "Expense") dispatch({ type: "direction", value: "Outgoing" });
    if (lockedTxnType === "Contra") dispatch({ type: "direction", value: "Outgoing" });

    // default date
    dispatch({ type: "cheque_date", value: new Date().toISOString().slice(0, 10) });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, lockedTxnType, bank?.bank_id, ledgerAccountId]);

  useEffect(() => {
    if (!show) {
      setNotify(null);
      dispatch({ type: "RESET", value: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!currentNotify) return;
    const t = setTimeout(() => setNotify(null), 5000);
    return () => clearTimeout(t);
  }, [currentNotify]);

  useEffect(()=>{
    getIncomeAccounts();
    getExpenseAccounts();
    getTagListing();
  }, []);

  const requireField = (condition, message) => {
    if (condition) {
      setNotify(<Notification type="danger" message={message} />);
      return true;
    }
    return false;
  };

  const accountingHint = useMemo(() => {
    const amt = Number(state.amount || 0);

    const sourceName = `${ledgerAccountName} (Cash at Bank)`;
    if (lockedTxnType === "Income") {
      return `Journal hint: Debit ${sourceName} ${currency} ${safeMoney(amt)}, Credit - ${state.ledger_account_id?.label || "Income (e.g. Sales/Revenue)"}.`;
    }
    if (lockedTxnType === "Expense") {
      return `Journal hint: Debit ${state.ledger_account_id?.label || "Expense (e.g. Supplies/Rent)"}, Credit ${sourceName} ${currency} ${safeMoney(amt)}.`;
    }
   
  }, [state.amount, state.ledger_account_id, ledgerAccountName, lockedTxnType, currency]);

  const payloadPreview = useMemo(() => {
    const base = {
      cheque_date: state.cheque_date,
      direction: state.direction,
      amount: String(state.amount || "").trim(),
      cash_account_id: bank?.account_id,
      beneficiary_id: state.beneficiary_id?.value || "",
      reference: String(state.reference || "").trim(),
      narration: String(state.narration || "").trim(),
      txn_type: state.txn_type, // locked,
      ledger_account_id: state.ledger_account_id?.value || "",
    };

    if (lockedTxnType === "Contra") {
      return { ...base, contra_cash_account_id: String(state.contra_cash_account_id || "").trim() };
    }
    return base;
  }, [state, lockedTxnType]);


  const handleSave = async () => {
  
    if (state.loading) return;

    // bank must exist since opened from bank card
    if (requireField(!ledgerAccountId, "Missing bank ledger account (account_id). Please reopen from a bank card.")) return;

    if (requireField(!String(state.cheque_date || "").trim(), "Select cheque date.")) return;
    if (requireField(!String(state.amount || "").trim(), "Enter amount, then try again.")) return;
    if (requireField(Number(state.amount) <= 0, "Amount must be greater than 0.")) return;

    // reference is important for cheques
    if (requireField(!String(state.reference || "").trim(), "Enter cheque number / reference.")) return;

    // beneficiary required for Deposit/Withdraw
    if (lockedTxnType !== "Contra") {
      if (requireField(!String(state.beneficiary_id || "").trim(), "Select beneficiary (Payer/Payee).")) return;
    }


    dispatch({ type: "loading", value: true });
    const server_response = await ajaxBanking.addCheque(payloadPreview);
    dispatch({ type: "loading", value: false });

    if (server_response?.status === "OK") {
      setNotify(<Notification type="success" message={server_response.message || "Cheque posted successfully."} />);
      onSaved(server_response);
      dispatch({ type: "RESET", value: null });
    } else {
      setNotify(<Notification type="danger" message={server_response?.message || "Failed to post cheque."} />);
    }
  };


  const RenderLedgerAccount = () => {

        const options = [];
        if(Array.isArray(incomeAccountListing) && txnType === "Deposit"){
            incomeAccountListing.map((item, key)=>
            options.push({label:item.account_number + ' ' + item.account_name, value:item.account_id})
            )
        }

        if(Array.isArray(expenseAccountListing) && txnType === "Withdraw"){
            expenseAccountListing.map((item, key)=>
            options.push({label:item.account_number + ' ' + item.account_name, value:item.account_id})
            )
        }

        return( <TextInput
                    label={txnType === "Deposit" ? "Income Account" : "Expense Account"}
                    type="select"
                    leftIcon="fa fa-university"
                    placeholder={txnType === "Deposit" ? "Income Account..." : "Expense Account..."}
                    options={options}
                    onChange={(item) => dispatch({type: 'ledger_account_id', value: item})}
                    leftComponent={<i className="fa fa-university link"></i>}
                    loading={txnType === "Deposit" ? !incomeAccountListing : !expenseAccountListing}
                    wrap={true}
                    value={state.ledger_account_id}
                    />
    
        )
    };

    const RenderTagSelector = () => {

        const options = [];
        if(Array.isArray(tagListing)){
            tagListing.map((item, key)=>
            options.push({label:item.tag_name, value:item.tag_id})
            )
        }

        return( <TextInput
                    label={"Tag (Optional)"}
                    type="select"
                    leftIcon="fa fa-tags"
                    placeholder={"Select tag..."}
                    options={options}
                    onChange={(item) => dispatch({type: 'tag_id', value: item})}
                    leftComponent={<i className="fa fa-tags link"></i>}
                    loading={!tagListing}
                    wrap={true}
                    value={state.tag_id}
                    />
    
        )
    }


  return (
    <BsModal
      show={show}
      onClose={onClose}
      title={modalTitle}
      size="modal-lg"
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={state.loading}>
            Cancel
          </button>

          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={state.loading}>
            {state.loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" />
                Posting...
              </>
            ) : (
              <>
                <i className="fa fa-check-circle mr-1" /> {buttonLabel}
              </>
            )}
          </button>
        </>
      }
    >
      {/* Bank context header */}
      <div
        className="p-3 mb-3"
        style={{
          borderRadius: 14,
          border: "1px solid rgba(255,255,255,0.08)",
          background: "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))",
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div
              className="mr-3 d-flex align-items-center justify-content-center"
              style={{
                width: 46,
                height: 46,
                borderRadius: 14,
                background: headerTone.bg,
                border: `1px solid ${headerTone.bd}`,
              }}
            >
              <i className={`${headerTone.icon} ${headerTone.text}`} />
            </div>

            <div>
              <div className="font-weight-bold" style={{ fontSize: 16 }}>
                {bankName} • {ledgerAccountName}
              </div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                A/C No: {bankAccountNumber || "—"} {bankBranch ? ` • ${bankBranch}` : ""}
              </div>
            </div>
          </div>

          <div className="text-right">
            <div className="font-weight-bold" style={{ fontSize: 14 }}>
              UGX {safeMoney(ledgerBalance)}
            </div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              Ledger balance
            </div>
          </div>
        </div>


        {/* accounting hint */}
        <div className="mt-3">
          <AccountingHint hint={accountingHint} />
        </div>
      </div>

      {currentNotify}

      {/* Core fields */}
      <div className="row">
        <div className="col-md-6">
          <TextInput
            type="date"
            label="Cheque Date"
            leftIcon="fa fa-calendar-alt"
            value={state.cheque_date}
            onChange={(e) => dispatch({ type: "cheque_date", value: e.target.value })}
            wrap
            required
          />
        </div>

        <div className="col-md-6">
          <TextInput
            type="text"
            label="Cheque No. / Reference"
            placeholder="e.g. 5032"
            leftIcon="fa fa-hashtag"
            value={state.reference}
            onChange={(e) => dispatch({ type: "reference", value: e.target.value })}
            wrap
            required
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-6">
          <TextInput
            type="number"
            label="Amount (UGX)"
            placeholder="e.g. 250000"
            leftIcon="fa fa-credit-card"
            value={state.amount}
            onChange={(e) => dispatch({ type: "amount", value: e.target.value })}
            wrap
            required
          />
        </div>

        <div className="col-md-6">
            { RenderLedgerAccount()}
        </div>
      </div>

      {/* Beneficiary (Deposit/Withdraw only) */}
      {lockedTxnType !== "Contra" && (
        <div className="row">
          <div className="col-md-6">
            <VendorSelector
                onChange={item => dispatch({type: 'beneficiary_id', value: item})}
                placeholder="Select beneficiary"
                label= {lockedTxnType === "Income" ? "Payer (Who gave you the cheque)" : "Payee (Who you’re paying)"}
                wrap
                showCreate={false}
                value={state.beneficiary_id}
                /> 
          </div>
          <div className="col-md-6">
            { RenderTagSelector()}
          </div>
        </div>
      )}

      {/* Narration */}
      <div className="row">
        <div className="col-md-12">
          <TextInput
            type="text"
            label="Narration (Memo)"
            placeholder="e.g. Payment for office supplies"
            leftIcon="fa fa-sticky-note"
            value={state.narration}
            onChange={(e) => dispatch({ type: "narration", value: e.target.value })}
            wrap
          />
        </div>
      </div>

    </BsModal>
  );
};

export default NewChequeTransactionModal;
